/**
 * <kr-ugc-card> — swaps a UGC card's still image for its video on first play.
 *
 * The <video> lives in a <template> until it is wanted, so a carousel of a dozen clips
 * costs nothing beyond the poster images on first paint — no elements to parse, no
 * metadata requests, not even a `preload="none"` hint to honour.
 *
 * Cards without a video never get a play button, so this only ever runs on demand.
 * Without JavaScript the still image and its caption remain, which is the graceful
 * outcome: the section is social proof first and video second.
 *
 * Refs:
 *   [data-kr-ugc-play]  — the play button overlay
 *   [data-kr-ugc-video] — <template> holding the <video>
 */

class KrUgcCard extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {HTMLVideoElement | null} */
  #video = null;

  connectedCallback() {
    this.#controller = new AbortController();

    const button = this.querySelector('[data-kr-ugc-play]');
    button?.addEventListener('click', this.#handlePlayClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#video?.pause();
    this.#video = null;
  }

  #handlePlayClick = () => {
    const video = this.#video ?? this.#mountVideo();
    if (!video) return;

    // Two clips talking over each other is never what a shopper wants.
    for (const other of document.querySelectorAll('kr-ugc-card video')) {
      if (other !== video && other instanceof HTMLVideoElement) other.pause();
    }

    this.toggleAttribute('playing', true);
    // Autoplay policies can still refuse this; the video keeps its own controls either way.
    video.play().catch(() => {});
  };

  /** @returns {HTMLVideoElement | null} */
  #mountVideo() {
    const template = this.querySelector('[data-kr-ugc-video]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment instanceof DocumentFragment ? fragment.querySelector('video') : null;
    if (!video) return null;

    template.replaceWith(fragment);
    this.#video = video;
    return video;
  }
}

if (!customElements.get('kr-ugc-card')) {
  customElements.define('kr-ugc-card', KrUgcCard);
}
